import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {useEffect, useState} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import ModalDraggable from '../common/ModalDraggable';

interface RemoteAccessData {
  safeye_node_id: string;
  type: string;
  name: string;
  ws_link_disp: string | null;
  ws_link_disp_updated_at: string | null;
  cameras: Array<{
    camera_index: number;
    ws_link: string;
    updated_at: string;
  }>;
}

interface Props {
  safeye_node_id: string;
  onClose: () => void;
}

const withProtocol = (url: string) =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `http://${url}`;

const CopyLinkButton = ({url}: {url: string}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available; silently ignore
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const LinkRow = ({label, url}: {label: string; url: string}) => {
  const href = withProtocol(url);
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{py: 0.5}}>
      <Typography variant="body2" sx={{minWidth: 120, fontWeight: 500}}>
        {label}
      </Typography>
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.875rem',
        }}
      >
        {href}
      </Link>
      <IconButton
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
      <CopyLinkButton url={href} />
    </Stack>
  );
};

const RemoteAccessModal = ({safeye_node_id, onClose}: Props) => {
  const [data, setData] = useState<RemoteAccessData | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErrors([]);
      try {
        const resp = await API.get<RemoteAccessData>(
          `${apiBaseUrl}/safeye-nano-node/${safeye_node_id}/remote-access`
        );
        setData(resp.data);
      } catch (error: any) {
        setErrors(getMessagesFromApiError(error, 'STRA_ra_001'));
      }
      setLoading(false);
    };
    fetchData();
  }, [safeye_node_id]);

  const hasLinks = data && (data.ws_link_disp || data.cameras.length > 0);

  return (
    <ModalDraggable open>
      <Paper sx={{minWidth: 480, maxWidth: 720, p: 3}}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{mb: 2}}
        >
          <Box>
            <Typography variant="h6">Remote Access</Typography>
            {data && (
              <Typography variant="caption" color="text.secondary">
                {data.name} — {data.safeye_node_id}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {loading && (
          <Stack alignItems="center" sx={{py: 4}}>
            <CircularProgress size={32} />
          </Stack>
        )}

        {errors.length > 0 && (
          <Alert severity="error" sx={{mb: 2}}>
            {errors.join(' ')}
          </Alert>
        )}

        {!loading && !errors.length && data && (
          <>
            {!hasLinks && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{py: 2, textAlign: 'center'}}
              >
                No active links available for this device.
              </Typography>
            )}

            {data.ws_link_disp && (
              <>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  sx={{mb: 0.5}}
                >
                  Display Stream
                </Typography>
                <LinkRow label="Display" url={data.ws_link_disp} />
              </>
            )}

            {data.cameras.length > 0 && (
              <>
                {data.ws_link_disp && <Divider sx={{my: 1.5}} />}
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  sx={{mb: 0.5}}
                >
                  Camera Streams
                </Typography>
                {data.cameras.map((cam) => (
                  <LinkRow
                    key={cam.camera_index}
                    label={`Camera ${cam.camera_index + 1}`}
                    url={cam.ws_link}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Paper>
    </ModalDraggable>
  );
};

export default RemoteAccessModal;
